/**
 * Lets a page ask before the browser's Back/Forward buttons leave it. BrowserRouter has no useBlocker, and on `window`
 * popstate listeners run in the order they were added, so this one must be installed before the router mounts: it then
 * runs first and, when the user stays, steps straight back to the page's history entry (React Router's `idx`) with the
 * router never seeing the move.
 */

let active: { idx: number; confirmLeave: () => boolean } | null = null
/** The entry we are stepping back to after a declined leave; that one popstate is ours, not a navigation. */
let returningTo: number | null = null
let installed = false

const idxOf = (state: unknown) => (state as { idx?: unknown } | null)?.idx

/** Call once at startup, before rendering the router. Repeat calls do nothing. */
export function installHistoryGuard(win: Window = window): void {
  if (installed) return
  installed = true
  win.addEventListener('popstate', (e) => {
    const to = idxOf(e.state)
    if (returningTo !== null) {
      const ours = to === returningTo
      returningTo = null
      if (ours) {
        e.stopImmediatePropagation()
        return
      }
    }
    if (!active || typeof to !== 'number' || to === active.idx || active.confirmLeave()) return
    e.stopImmediatePropagation()
    returningTo = active.idx
    win.history.go(active.idx - to)
  })
}

/** Guards the current history entry until the returned function is called. `confirmLeave` returns true to let the user go. */
export function guardHistory(confirmLeave: () => boolean): () => void {
  const idx = idxOf(window.history.state)
  if (typeof idx !== 'number') return () => {}
  const entry = { idx, confirmLeave }
  active = entry
  return () => {
    if (active === entry) active = null
  }
}
